import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Play, Pause, SkipBack, SkipForward, Maximize2 } from 'lucide-react';

interface SwingFrame {
  frame_index: number;
  time_s: number;
  keypoints: any;
}

interface PoseFrameViewerProps {
  frames: SwingFrame[];
}

// Golf pose connections for skeleton drawing
const POSE_CONNECTIONS = [
  [11, 12], // shoulders
  [11, 13], [13, 15], // left arm
  [12, 14], [14, 16], // right arm
  [11, 23], [12, 24], // torso
  [23, 24], // hips
  [23, 25], [25, 27], // left leg
  [24, 26], [26, 28], // right leg
];

export function PoseFrameViewer({ frames }: PoseFrameViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedKeypoint, setSelectedKeypoint] = useState<number | null>(null);

  useEffect(() => {
    if (frames.length > 0) {
      drawPoseFrame(currentFrame);
    }
  }, [currentFrame, frames]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && frames.length > 1) {
      interval = setInterval(() => {
        setCurrentFrame((prev) => {
          const next = prev + 1;
          if (next >= frames.length) {
            setIsPlaying(false);
            return 0;
          }
          return next;
        });
      }, 200); // 5 FPS playback
    }
    return () => clearInterval(interval);
  }, [isPlaying, frames.length]);

  const drawPoseFrame = (frameIndex: number) => {
    const canvas = canvasRef.current;
    if (!canvas || !frames[frameIndex]) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const frame = frames[frameIndex];
    if (!frame || !frame.keypoints) return;
    
    const landmarks = frame.keypoints;
    
    if (!landmarks || !Array.isArray(landmarks)) return;

    // Set canvas size
    canvas.width = 400;
    canvas.height = 400;

    // Draw connections
    ctx.strokeStyle = 'hsl(156 89% 18%)';
    ctx.lineWidth = 2;
    
    POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
      const start = landmarks[startIdx];
      const end = landmarks[endIdx];
      
      if (start && end && start.visibility > 0.5 && end.visibility > 0.5) {
        ctx.beginPath();
        ctx.moveTo(start.x * canvas.width, start.y * canvas.height);
        ctx.lineTo(end.x * canvas.width, end.y * canvas.height);
        ctx.stroke();
      }
    });

    // Draw keypoints
    landmarks.forEach((landmark: any, index: number) => {
      if (!landmark || typeof landmark.visibility !== 'number' || landmark.visibility < 0.5) return;
      if (typeof landmark.x !== 'number' || typeof landmark.y !== 'number') return;
      
      const x = landmark.x * canvas.width;
      const y = landmark.y * canvas.height;
      
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, 2 * Math.PI);
      
      if (index === selectedKeypoint) {
        ctx.fillStyle = 'hsl(0 75% 55%)';
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.fill();
        ctx.stroke();
      } else {
        const confidence = landmark.visibility || 0;
        const alpha = Math.max(0.3, confidence);
        ctx.fillStyle = `hsla(156, 89%, 13%, ${alpha})`;
        ctx.fill();
      }
    });
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !frames[currentFrame]) return;

    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    
    const landmarks = frames[currentFrame].keypoints;
    if (!landmarks) return;

    // Find closest landmark
    let closestIndex = -1;
    let closestDistance = Infinity;
    
    landmarks.forEach((landmark: any, index: number) => {
      if (!landmark || landmark.visibility < 0.5) return;
      
      const distance = Math.sqrt(
        Math.pow(landmark.x - x, 2) + Math.pow(landmark.y - y, 2)
      );
      
      if (distance < 0.05 && distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    
    setSelectedKeypoint(closestIndex >= 0 ? closestIndex : null);
  };

  if (frames.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center text-muted-foreground">
            No frame data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentFrameData = frames[currentFrame];
  const selectedLandmark = selectedKeypoint !== null && currentFrameData?.keypoints?.[selectedKeypoint];

  return (
    <div className="space-y-6">
      {/* Pose Visualization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Pose Visualization</span>
            <Badge variant="outline">
              Frame {currentFrame + 1} of {frames.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Canvas */}
            <div className="flex-1">
              <canvas
                ref={canvasRef}
                className="border border-border rounded-lg cursor-pointer max-w-full h-auto bg-card"
                onClick={handleCanvasClick}
                style={{ aspectRatio: '1' }}
              />
            </div>
            
            {/* Frame Info */}
            <div className="md:w-64 space-y-4">
              <div>
                <h4 className="font-medium text-foreground mb-2">Frame Info</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Index:</span>
                    <span>{currentFrameData?.frame_index}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time:</span>
                    <span>{currentFrameData?.time_s?.toFixed(3)}s</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Keypoints:</span>
                    <span>{currentFrameData?.keypoints?.length || 0}</span>
                  </div>
                </div>
              </div>
              
              {selectedLandmark && (
                <div>
                  <h4 className="font-medium text-foreground mb-2">
                    Selected Keypoint #{selectedKeypoint}
                  </h4>
                  <div className="space-y-2 text-sm">
                   <div className="flex justify-between">
                     <span className="text-muted-foreground">X:</span>
                     <span>{typeof selectedLandmark.x === 'number' ? selectedLandmark.x.toFixed(3) : 'N/A'}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-muted-foreground">Y:</span>
                     <span>{typeof selectedLandmark.y === 'number' ? selectedLandmark.y.toFixed(3) : 'N/A'}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-muted-foreground">Z:</span>
                     <span>{typeof selectedLandmark.z === 'number' ? selectedLandmark.z.toFixed(3) : 'N/A'}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-muted-foreground">Confidence:</span>
                     <span>{typeof selectedLandmark.visibility === 'number' ? (selectedLandmark.visibility * 100).toFixed(1) + '%' : 'N/A'}</span>
                   </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Playback Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Timeline Slider */}
            <div className="space-y-2">
              <Slider
                value={[currentFrame]}
                onValueChange={(value) => setCurrentFrame(value[0])}
                max={frames.length - 1}
                step={1}
                className="w-full"
              />
               <div className="flex justify-between text-xs text-muted-foreground">
                 <span>0s</span>
                 <span>
                   {frames[frames.length - 1]?.time_s && typeof frames[frames.length - 1].time_s === 'number' 
                     ? frames[frames.length - 1].time_s.toFixed(1) + 's' 
                     : 'N/A'}
                 </span>
               </div>
            </div>
            
            {/* Control Buttons */}
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentFrame(0)}
                disabled={currentFrame === 0}
              >
                <SkipBack className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentFrame(Math.max(0, currentFrame - 1))}
                disabled={currentFrame === 0}
              >
                Previous
              </Button>
              
              <Button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={frames.length <= 1}
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4 mr-2" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentFrame(Math.min(frames.length - 1, currentFrame + 1))}
                disabled={currentFrame === frames.length - 1}
              >
                Next
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentFrame(frames.length - 1)}
                disabled={currentFrame === frames.length - 1}
              >
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}